import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import MDButton from "components/MDButton";
import leaseCompaniesService, { LeaseCompany } from "services/leaseCompaniesService";

const LeaseCompaniesAdmin = () => {
  const [companies, setCompanies] = useState<LeaseCompany[]>([]);
  const [filter, setFilter] = useState<string>("active");
  const [modalOpen, setModalOpen] = useState(false);
  const [newName, setNewName] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, [filter]);

  const load = async () => {
    try {
      const includeInactive = filter !== "active";
      const all = await leaseCompaniesService.getLeaseCompanies(includeInactive);
      const filtered = filter === "inactive" ? all.filter((c) => !c.isActive) : all;
      setCompanies(filtered);
    } catch {
      toast.error("Failed to load lease companies");
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a company name");
      return;
    }
    setSaving(true);
    try {
      await leaseCompaniesService.createLeaseCompany(name);
      toast.success("Lease company added");
      setModalOpen(false);
      setNewName("");
      load();
    } catch {
      toast.error("Failed to add lease company");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: LeaseCompany) => {
    try {
      await leaseCompaniesService.setLeaseCompanyActive(c.id, !c.isActive);
      load();
    } catch {
      toast.error("Failed to update lease company");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Vehicle Lease Companies</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter}
              label="Status"
              size="small"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <MDButton variant="contained" color="primary" onClick={() => setModalOpen(true)}>
            Add Lease Company
          </MDButton>
        </Box>
      </Box>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Lease Company</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" onClick={() => setModalOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="primary" onClick={handleCreate} disabled={saving}>
            {saving ? "Saving..." : "Add"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>
                  <Switch checked={c.isActive} onChange={() => toggleActive(c)} color="primary" />
                </TableCell>
              </TableRow>
            ))}
            {companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
                    No lease companies to show.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default LeaseCompaniesAdmin;
